import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, BellOff, Check, X, AlertTriangle, Smartphone, Monitor, Loader2,
  Zap, RefreshCw, ArrowLeft, ExternalLink, Apple, Globe,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { enableWebPush, disableWebPush, pushSupported } from "../lib/webpush";

/**
 * NotificationHealth — Wave 25.15.
 * Diagnose and FIX mobile push notifications. Detects the user's platform
 * (iOS / Android / Desktop), the install state (PWA vs browser tab), the
 * permission state, and the SW + subscription state — then walks them
 * through the exact steps needed for "WhatsApp-grade" reliability.
 *
 * Common cause map (what we surface on this page):
 *   • iOS Safari, NOT installed as PWA   → push physically can't work
 *   • Android Chrome, browser tab        → push works but SW killed by battery saver
 *   • permission denied                   → user must reset in browser settings
 *   • SW unregistered / sub missing      → one-tap re-subscribe
 *   • test push fails to arrive          → likely OS-level "Focus / DND"
 */
export default function NotificationHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const h = await diagnose();
    setHealth(h);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  // Listen for the SW saying it received a push (best-effort signal).
  useEffect(() => {
    const onMessage = (e) => {
      if (e?.data?.type === "HV_PUSH_RECEIVED") {
        toast.success("Push received! Notifications are working.");
        refresh();
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onMessage);
      return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }
  }, []);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const res = await enableWebPush();
      if (res.ok) {
        toast.success("Notifications enabled.");
      } else {
        toast.error(`Couldn't enable: ${res.reason || "unknown"}`);
      }
      await refresh();
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Turn off push notifications on this device?")) return;
    await disableWebPush();
    toast("Notifications disabled on this device.");
    await refresh();
  };

  const sendTest = async () => {
    setTesting(true);
    let arrived = false;
    const onPushed = (e) => {
      if (e?.data?.type === "HV_PUSH_RECEIVED") arrived = true;
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onPushed);
    }
    try {
      await api.post("/push/test");
      toast("Test push sent. Watching for arrival…");
      await new Promise((r) => setTimeout(r, 12000));
      if (arrived) {
        toast.success("✅ Test push arrived. You're all set.");
      } else {
        toast.error("⚠️ Sent, but didn't arrive in 12 s. See troubleshooting below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send test push.");
    } finally {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onPushed);
      }
      setTesting(false);
    }
  };

  if (loading || !health) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="notif-health-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#1FB6C1]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5" data-testid="notif-health-page">
      <div className="flex items-center gap-2">
        <Link to="/settings" className="hv-btn hv-btn-ghost text-xs" data-testid="notif-health-back">
          <ArrowLeft className="w-3 h-3" /> Settings
        </Link>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#1FB6C1]" /> Notification health
        </h1>
        <button
          type="button"
          onClick={refresh}
          className="hv-btn hv-btn-ghost text-xs ml-auto"
          title="Re-check"
          data-testid="notif-health-refresh"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      {/* Status grid */}
      <div className="hv-card p-5 space-y-3" data-testid="notif-health-status">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-300" /> Status on this device
        </h2>
        <CheckRow label="Browser supports push"  ok={health.supported} />
        <CheckRow label={`Platform: ${health.platformLabel}`} ok={true} icon={health.platformIcon} />
        <CheckRow label="Installed as app (PWA)" ok={health.standalone} hint={!health.standalone && health.platform === "ios" ? "REQUIRED on iOS — push won't work otherwise" : undefined} />
        <CheckRow
          label={
            health.permission === "granted"
              ? "Notification permission granted"
              : health.permission === "denied"
              ? "Notification permission DENIED"
              : "Notification permission not yet granted"
          }
          ok={health.permission === "granted"}
          hint={health.permission === "denied" ? "Reset in browser settings (see below)" : undefined}
        />
        <CheckRow label="Service worker registered" ok={health.swActive} />
        <CheckRow label="Push subscription active" ok={health.subscribed} />
        <CheckRow label="Server has this device on file" ok={health.serverHasMe} />
      </div>

      {/* Action card */}
      {!health.subscribed || health.permission !== "granted" ? (
        <div className="hv-card p-5 space-y-3" data-testid="notif-health-enable">
          <h2 className="font-display text-lg font-bold">Enable notifications</h2>
          <p className="text-sm text-[#a0aabf]">
            Tap the button below. Your browser will ask for permission — tap <strong>Allow</strong>.
          </p>
          <button
            type="button"
            onClick={handleEnable}
            disabled={enabling || !health.supported}
            className="hv-btn hv-btn-primary"
            data-testid="notif-health-enable-btn"
          >
            {enabling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            {enabling ? "Enabling…" : "Enable notifications"}
          </button>
        </div>
      ) : (
        <div className="hv-card p-5 space-y-3" data-testid="notif-health-test">
          <h2 className="font-display text-lg font-bold">Send a test push</h2>
          <p className="text-sm text-[#a0aabf]">
            Tests the full chain: server → push gateway → your device. Should arrive within ~10 seconds.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={sendTest}
              disabled={testing}
              className="hv-btn hv-btn-primary"
              data-testid="notif-health-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {testing ? "Watching for arrival…" : "Send test push"}
            </button>
            <button
              type="button"
              onClick={handleDisable}
              className="hv-btn hv-btn-ghost text-rose-300"
              data-testid="notif-health-disable-btn"
            >
              <BellOff className="w-4 h-4" /> Turn off on this device
            </button>
          </div>
        </div>
      )}

      {/* Platform-specific troubleshooting */}
      <PlatformGuide health={health} />

      {/* All my devices */}
      <DeviceList />
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────────────────── */

function CheckRow({ label, ok, hint, icon }) {
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={`check-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="mt-0.5">
        {icon || (ok ? <Check className="w-4 h-4 text-emerald-300" /> : <X className="w-4 h-4 text-rose-300" />)}
      </span>
      <div className="flex-1">
        <div className={ok ? "text-white" : "text-rose-200"}>{label}</div>
        {hint && <div className="text-[11px] text-amber-300 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function PlatformGuide({ health }) {
  if (!health) return null;
  const isIOS = health.platform === "ios";
  const isAndroid = health.platform === "android";
  return (
    <div className="hv-card p-5 space-y-3" data-testid="notif-platform-guide">
      <h2 className="font-display text-lg font-bold flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-300" /> Make sure pushes always come through
      </h2>
      <div className="space-y-3 text-sm leading-relaxed">
        {isIOS && (
          <Step n={1} title="Install as a PWA (REQUIRED on iPhone)">
            iOS only delivers web push to <strong>installed</strong> apps.
            <ol className="list-decimal ml-5 mt-1 space-y-1 text-[#a0aabf]">
              <li>Open SalesFlow in Safari (not Chrome — Chrome on iOS can't do push).</li>
              <li>Tap the <strong>Share</strong> button (square with up arrow).</li>
              <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
              <li>Open SalesFlow from the Home Screen icon, log in, then return here and tap Enable.</li>
            </ol>
          </Step>
        )}
        {isAndroid && (
          <>
            <Step n={1} title="Install as an app (recommended)">
              Use the install option in Chrome's menu (⋮ → "Install app"). Pushes
              survive battery savers much better in PWA mode than in a tab.
            </Step>
            <Step n={2} title="Disable battery optimization for Chrome / SalesFlow">
              <ol className="list-decimal ml-5 mt-1 space-y-1 text-[#a0aabf]">
                <li>Phone Settings → Apps → Chrome (or SalesFlow if installed) → Battery</li>
                <li>Set to <strong>"Unrestricted"</strong> (Samsung) or <strong>"Don't optimise"</strong> (stock Android).</li>
                <li>This stops Android from killing the background service worker.</li>
              </ol>
            </Step>
            <Step n={3} title="Allow critical alerts even in Do Not Disturb">
              Settings → Notifications → Do Not Disturb → Apps → add Chrome / SalesFlow as exception.
            </Step>
          </>
        )}
        {!isIOS && !isAndroid && (
          <Step n={1} title="Desktop browsers">
            Pushes work great. Just make sure you didn't accidentally block the site
            in <code className="text-[#1FB6C1]">chrome://settings/content/notifications</code> (Chrome) or
            Settings → Privacy → Notifications (Firefox / Edge).
          </Step>
        )}
        <Step n={isAndroid ? 4 : 2} title="If permission was denied earlier">
          The browser remembers your "Block" choice — you have to reset it manually.
          <ol className="list-decimal ml-5 mt-1 space-y-1 text-[#a0aabf]">
            <li>Tap the lock 🔒 / info icon in the address bar.</li>
            <li>Find <strong>Notifications</strong> and reset / set to <strong>Allow</strong>.</li>
            <li>Reload this page and tap Enable.</li>
          </ol>
        </Step>
        <Step n={isAndroid ? 5 : 3} title="Always-on tip">
          Keep SalesFlow open in at least one browser tab (or installed PWA) for the
          most reliable delivery. Most failed pushes are caused by the OS aggressively
          freezing the browser after hours of inactivity.
        </Step>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="border-l-2 border-[#1FB6C1]/40 pl-3 py-1">
      <div className="text-xs uppercase tracking-wider text-[#1FB6C1] font-mono">Step {n}</div>
      <div className="font-bold text-white text-sm mt-0.5">{title}</div>
      <div className="text-sm text-[#a0aabf] mt-1">{children}</div>
    </div>
  );
}

function DeviceList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/push/subscriptions");
        setRows(data?.items || []);
      } catch (_) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return null;
  if (rows.length === 0) return null;
  return (
    <div className="hv-card p-5" data-testid="notif-devices">
      <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-2">
        <Smartphone className="w-4 h-4" /> Your subscribed devices
      </h2>
      <ul className="space-y-1.5 text-xs">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-black/20 border border-white/10">
            {r.active ? (
              <Check className="w-3.5 h-3.5 text-emerald-300" />
            ) : (
              <X className="w-3.5 h-3.5 text-rose-300" />
            )}
            <span className="flex-1 truncate text-[#a0aabf]" title={r.user_agent || ""}>
              {shortenUA(r.user_agent)}
            </span>
            <span className="text-[10px] text-[#647087] font-mono">
              {new Date(r.updated_at || r.created_at || Date.now()).toLocaleDateString('en-GB')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function shortenUA(ua) {
  if (!ua) return "Unknown device";
  const s = ua;
  if (/iPhone/i.test(s)) return "iPhone (Safari)";
  if (/iPad/i.test(s)) return "iPad (Safari)";
  if (/Android/i.test(s) && /Chrome/i.test(s)) return "Android Chrome";
  if (/Android/i.test(s)) return "Android browser";
  if (/Macintosh/i.test(s) && /Safari/i.test(s) && !/Chrome/i.test(s)) return "Mac Safari";
  if (/Chrome/i.test(s)) return "Chrome (desktop)";
  if (/Firefox/i.test(s)) return "Firefox";
  if (/Edg/i.test(s)) return "Edge";
  return s.slice(0, 50);
}

async function diagnose() {
  const supported = pushSupported();
  const ua = navigator.userAgent || "";
  let platform = "desktop";
  let platformLabel = "Desktop browser";
  let platformIcon = <Monitor className="w-4 h-4 text-sky-300" />;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    platform = "ios";
    platformLabel = "iOS Safari";
    platformIcon = <Apple className="w-4 h-4 text-sky-300" />;
  } else if (/Android/i.test(ua)) {
    platform = "android";
    platformLabel = "Android";
    platformIcon = <Smartphone className="w-4 h-4 text-sky-300" />;
  } else if (/Mac/i.test(ua)) {
    platformLabel = "macOS";
  } else if (/Windows/i.test(ua)) {
    platformLabel = "Windows";
  } else if (/Linux/i.test(ua)) {
    platformLabel = "Linux";
  }
  // PWA standalone detection — tricky because each platform uses a different signal.
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true;
  const permission =
    typeof Notification !== "undefined" ? Notification.permission : "unsupported";
  let swActive = false;
  let subscribed = false;
  if (supported) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      swActive = !!(reg && reg.active);
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        subscribed = !!sub;
      }
    } catch (_) { /* ignore */ }
  }
  // Server-side cross-check: do we have any active sub for this user?
  let serverHasMe = false;
  try {
    const { data } = await api.get("/push/subscriptions");
    serverHasMe = (data?.active_count || 0) > 0;
  } catch (_) { /* ignore */ }
  return {
    supported, platform, platformLabel, platformIcon, standalone,
    permission, swActive, subscribed, serverHasMe,
  };
}
